import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check, ChevronDown, Columns3, Copy, Download, Eye, EyeOff, Filter, Paintbrush, Pencil, Plus, Rows3, Search,
  Share2, Shield, Trash2, Type, Upload, X, ArrowLeft,
} from 'lucide-react';
import {
  api, COLUMN_TYPES, type CellStyle, type Column, type ColumnType, type Rec,
} from '../lib/api';
import { keys, useColumns, useDoc, useRecords } from '../lib/queries';
import { useUndoableRecords } from '../lib/useUndoableRecords';
import { uniqueValues, useView, type Sort } from '../lib/useView';
import { useComputed } from '../lib/formula/useComputed';
import { useRealtime } from '../lib/useRealtime';
import { docIcon } from '../lib/doc-icons';
import {
  COLUMN_TYPE_LABELS, R, btnAccent, btnOutline, chip, colLabel, ft, label, mono,
  plural, shadow,
} from '../lib/ui';
import { Grid, type Sel, moveColumnByDirection } from '../components/Grid';
import { Spinner } from '../components/Spinner';
import { MenuButton, InlineEdit } from '../components/MenuBar';
import { Select } from '../components/Select';
import { useContextMenu, type MenuEntry } from '../components/ContextMenu';
import { useDialog } from '../components/DialogHost';
import { useToast } from '../components/Toast';
import { HistoryPanel } from '../components/HistoryPanel';
import { ColumnPanel } from '../components/ColumnPanel';
import { FormatToolbar } from '../components/FormatToolbar';
import { FormulaBar } from '../components/FormulaBar';
import { ImportDialog } from '../components/ImportDialog';
import { ConditionalDialog } from '../components/ConditionalDialog';
import { SharePanel } from '../components/SharePanel';
import { StatusBar, useSelectionStats } from '../components/StatusBar';
import { DocPropertiesModal } from '../components/DocPropertiesModal';
import { NewSheetModal } from '../components/NewSheetModal';
import { LinkModal } from '../components/LinkModal';
import { TableAccessModal } from '../components/TableAccessModal';

export function DocView({
  docId, tableId, canEdit,
}: {
  docId: string;
  tableId?: string;
  canEdit: boolean;
}) {
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const toast = useToast();
  const dialog = useDialog();
  const tabMenu = useContextMenu();

  const doc = useDoc(docId);
  const tables = doc.data?.tables ?? [];
  const activeId = tableId ?? tables[0]?.id ?? '';
  const activeTable = tables.find((t) => t.id === activeId);
  const effectiveCanEdit = activeTable?.canEdit !== undefined ? activeTable.canEdit : canEdit;
  const canManageTableAccess = doc.data?.canManageAccess ?? canEdit;

  useEffect(() => {
    if (!tableId && activeId) navigate(`/d/${docId}/t/${activeId}`, { replace: true });
  }, [tableId, activeId, docId, navigate]);

  const columns = useColumns(docId, activeId);
  const records = useRecords(docId, activeId);

  const styles = useQuery({
    queryKey: ['styles', docId, activeId],
    queryFn: () => api.cellStyles(docId, activeId),
    enabled: !!activeId,
  });
  const merges = useQuery({
    queryKey: ['merges', docId, activeId],
    queryFn: () => api.cellMerges(docId, activeId),
    enabled: !!activeId,
  });
  const conditional = useQuery({
    queryKey: ['conditional', docId, activeId],
    queryFn: () => api.conditionalRules(docId, activeId),
    enabled: !!activeId,
  });
  const sizes = useQuery({
    queryKey: ['sizes', docId, activeId],
    queryFn: () => api.tableSizes(docId, activeId),
    enabled: !!activeId,
  });

  const history = useUndoableRecords(docId, activeId);

  // Кто ещё смотрит эту таблицу прямо сейчас.
  const viewers = useRealtime(docId, activeId);

  const [sel, setSel] = useState<Sel | null>(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchColId, setSearchColId] = useState<string | null>(null);
  const [filters, setFilters] = useState<Map<string, Set<string>>>(new Map());
  const [showHistory, setShowHistory] = useState(false);
  const [columnFor, setColumnFor] = useState<Column | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showProps, setShowProps] = useState(false);
  const [showNewSheet, setShowNewSheet] = useState(false);
  const [showConditional, setShowConditional] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [accessModalTable, setAccessModalTable] = useState<{ id: string; label: string } | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const allSheetsRecords = useQueries({
    queries: tables.map((t) => ({
      queryKey: keys.records(docId, t.id),
      queryFn: () => api.records(docId, t.id),
      enabled: !!docId && !!t.id && tables.length > 1,
      staleTime: 30_000,
    })),
  });

  const allSheetsColumns = useQueries({
    queries: tables.map((t) => ({
      queryKey: keys.columns(docId, t.id),
      queryFn: () => api.columns(docId, t.id),
      enabled: !!docId && !!t.id && tables.length > 1,
      staleTime: 60_000,
    })),
  });

  const allSheets = useMemo(() => {
    if (tables.length <= 1) return undefined;
    return tables.map((t, idx) => ({
      id: t.id,
      name: t.label,
      columns: t.id === activeId ? (columns.data ?? []) : (allSheetsColumns[idx]?.data ?? []),
      records: t.id === activeId ? (records.data ?? []) : (allSheetsRecords[idx]?.data ?? []),
    }));
  }, [tables, activeId, columns.data, records.data, allSheetsColumns, allSheetsRecords]);

  const computed = useComputed(columns.data ?? [], records.data ?? [], allSheets, activeTable?.label);
  const view = useView(
    columns.data ?? [],
    records.data ?? [],
    query,
    sort,
    computed,
    null,
    filters,
  );
  const reordered = !!query.trim() || !!sort || filters.size > 0;

  const styleMap = useMemo(() => {
    const map = new Map<string, CellStyle>();
    for (const s of styles.data ?? []) map.set(`${s.recordId}:${s.colId}`, s.style);
    return map;
  }, [styles.data]);

  const colWidths = useMemo(
    () => new Map((sizes.data?.colWidths ?? [])
        .filter((c): c is { colId: string; width: number } => c.width !== null)
        .map((c) => [c.colId, c.width])),
    [sizes.data],
  );
  const rowHeights = useMemo(
    () => new Map((sizes.data?.rowHeights ?? [])
        .filter((r): r is { recordId: string; height: number } => r.height !== null)
        .map((r) => [r.recordId, r.height])),
    [sizes.data],
  );

  const columnLabel = (colId: string) =>
    (columns.data ?? []).find((c) => c.fields.colId === colId)?.fields.label ?? colId;

  const valuesFor = (colId: string) =>
    uniqueValues(
      (columns.data ?? []).find((c) => c.fields.colId === colId),
      records.data ?? [],
      computed,
    );

  const setFilter = (colId: string, next: Set<string> | null) =>
    setFilters((prev) => {
      const map = new Map(prev);
      if (next) map.set(colId, next);
      else map.delete(colId);
      return map;
    });

  const [hiddenColIds, setHiddenColIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(`rt-hidden-cols:${activeId}`);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    if (!activeId) return;
    try {
      const raw = localStorage.getItem(`rt-hidden-cols:${activeId}`);
      setHiddenColIds(raw ? new Set(JSON.parse(raw)) : new Set());
    } catch {
      setHiddenColIds(new Set());
    }
  }, [activeId]);

  const toggleHideColumn = useCallback((colId: string) => {
    setHiddenColIds((prev) => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      if (activeId) {
        try {
          localStorage.setItem(`rt-hidden-cols:${activeId}`, JSON.stringify([...next]));
        } catch {}
      }
      return next;
    });
  }, [activeId]);

  const unhideAllColumns = useCallback(() => {
    setHiddenColIds(new Set());
    if (activeId) {
      try {
        localStorage.removeItem(`rt-hidden-cols:${activeId}`);
      } catch {}
    }
  }, [activeId]);

  const unhideColumns = useCallback((colIds: string[]) => {
    setHiddenColIds((prev) => {
      const next = new Set(prev);
      for (const id of colIds) next.delete(id);
      if (activeId) {
        try {
          localStorage.setItem(`rt-hidden-cols:${activeId}`, JSON.stringify([...next]));
        } catch {}
      }
      return next;
    });
  }, [activeId]);

  const visibleColsList = useMemo(() => {
    return (columns.data ?? []).filter((c) => !hiddenColIds.has(c.fields.colId));
  }, [columns.data, hiddenColIds]);

  const activeColumn = sel ? visibleColsList[sel.c1] ?? null : null;

  const selectionStats = useSelectionStats(sel, visibleColsList, view, computed);

  const renameDoc = useMutation({
    mutationFn: (name: string) => api.updateDoc(docId, { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.doc(docId) });
      qc.invalidateQueries({ queryKey: ['docs'] });
    },
  });

  const removeDoc = useMutation({
    mutationFn: () => api.deleteDoc(docId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['docs'] });
      navigate('/');
    },
  });

  const renameTable = useMutation({
    mutationFn: (v: { tableId: string; label: string }) =>
      api.updateTable(docId, v.tableId, { label: v.label }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.doc(docId) }),
  });

  const removeTable = useMutation({
    mutationFn: (id: string) => api.deleteTable(docId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.doc(docId) });
      navigate(`/d/${docId}`);
    },
  });

  const duplicateTable = useMutation({
    mutationFn: (v: { tableId: string; label?: string }) =>
      api.duplicateTable(docId, v.tableId, { label: v.label }),
    onSuccess: (newTab) => {
      qc.invalidateQueries({ queryKey: keys.doc(docId) });
      toast('Копия листа создана', 'ok');
      navigate(`/d/${docId}/t/${newTab.id}`);
    },
    onError: (e) => toast(`Ошибка создания копии листа: ${(e as Error).message}`, 'err'),
  });

  const duplicateDoc = useMutation({
    mutationFn: (name?: string) => api.duplicateDoc(docId, { name }),
    onSuccess: (newDoc) => {
      qc.invalidateQueries({ queryKey: ['docs'] });
      toast('Копия таблицы создана в том же разделе', 'ok');
      navigate(`/d/${newDoc.id}`);
    },
    onError: (e) => toast(`Ошибка создания копии: ${(e as Error).message}`, 'err'),
  });

  const handleRenameTable = async (t: { id: string; label: string }) => {
    const next = await dialog.prompt({
      title: 'Переименовать лист',
      value: t.label,
    });
    if (next?.trim() && next.trim() !== t.label) {
      renameTable.mutate({ tableId: t.id, label: next.trim() });
    }
  };

  const addColumn = useMutation({
    mutationFn: (data?: { label?: string; position?: number }) =>
      api.createColumn(docId, activeId, data ?? {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.columns(docId, activeId) }),
    onError: (e) => toast(`Не удалось создать колонку: ${(e as Error).message}`, 'err'),
  });

  const reorderCols = useMutation({
    mutationFn: (colIds: string[]) => api.reorderColumns(docId, activeId, colIds),
    onMutate: async (newColIds) => {
      await qc.cancelQueries({ queryKey: keys.columns(docId, activeId) });
      const prev = qc.getQueryData<Column[]>(keys.columns(docId, activeId));
      if (prev) {
        const idMap = new Map(prev.map((c) => [c.fields.colId, c]));
        const optimistic: Column[] = [];
        for (let i = 0; i < newColIds.length; i++) {
          const col = idMap.get(newColIds[i]);
          if (col) {
            optimistic.push({ ...col, fields: { ...col.fields, position: i } });
          }
        }
        qc.setQueryData(keys.columns(docId, activeId), optimistic);
      }
      return { prev };
    },
    onError: (err, _newCols, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(keys.columns(docId, activeId), ctx.prev);
      }
      toast(`Не удалось переместить колонки: ${(err as Error).message}`, 'err');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: keys.columns(docId, activeId) });
    },
  });

  const renameColumn = useMutation({
    mutationFn: (v: { colId: string; label: string }) =>
      api.updateColumn(docId, activeId, v.colId, { label: v.label }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.columns(docId, activeId) }),
    onError: (e) => toast((e as Error).message, 'err'),
  });

  const deleteColumn = useMutation({
    mutationFn: (colId: string) => api.deleteColumn(docId, activeId, colId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.columns(docId, activeId) });
      qc.invalidateQueries({ queryKey: keys.records(docId, activeId) });
      toast('Колонка удалена', 'ok');
    },
    onError: (e) => toast(`Не удалось удалить колонку: ${(e as Error).message}`, 'err'),
  });

  const resizeColumn = useMutation({
    mutationFn: (v: { colId: string; width: number | null }) =>
      api.patchTableSizes(docId, activeId, { colWidths: [v] }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sizes', docId, activeId] }),
  });

  const resizeRow = useMutation({
    mutationFn: (v: { recordId: string; height: number | null }) =>
      api.patchTableSizes(docId, activeId, { rowHeights: [v] }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sizes', docId, activeId] }),
  });

  const markSaved = useCallback(
    () => setSavedAt(new Date().toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })),
    [],
  );

  /** Картинка грузится на сервер, в ячейку попадает её адрес. */
  const insertImage = () => {
    if (!sel) return;
    const rec = view[sel.r1];
    const col = (columns.data ?? [])[sel.c1];
    if (!rec || !col || col.fields.isFormula) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/gif,image/webp,image/svg+xml';

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      if (file.size > 1024 * 1024) {
        toast('Картинка больше 1 МБ', 'err');
        return;
      }
      try {
        const uploaded = await api.uploadImage(file);
        await history.patch(
          [{ id: rec.id, fields: { [col.fields.colId]: uploaded.url } }],
          'Вставить картинку',
        );
        markSaved();
      } catch (e) {
        toast((e as Error).message, 'err');
      }
    };

    input.click();
  };

  const onPatch = useCallback(
    (rows: Rec[]) =>
      history
        .patch(rows)
        .then(markSaved)
        .catch((e) => toast((e as Error).message, 'err')),
    [history, markSaved, toast],
  );

  const onCreate = useCallback(
    (rows: { fields: Record<string, unknown> }[], afterId?: string) =>
      history
        .create(rows, afterId)
        .then(() => {
          markSaved();
          toast(`Добавлено строк: ${rows.length}`, 'ok');
        })
        .catch((e) => toast((e as Error).message, 'err')),
    [history, markSaved, toast],
  );

  const removeRows = useCallback(
    async (ids: string[]) => {
      if (!ids.length) return;
      const ok = await dialog.confirm({
        title: ids.length > 1 ? 'Удалить строки?' : 'Удалить строку?',
        body:
          ids.length > 1
            ? `Будет удалено строк: ${ids.length}. Отменить можно через Ctrl+Z.`
            : 'Строка будет удалена. Отменить можно через Ctrl+Z.',
        danger: true,
      });
      if (!ok) return;

      history
        .remove(ids)
        .then((res) => toast(`Удалено: ${res.deleted}`, 'ok'))
        .catch((e) => toast((e as Error).message, 'err'));
    },
    [dialog, history, toast],
  );

  const deleteSelectedRows = async () => {
    if (!sel) return;
    const ids = view.slice(sel.r1, sel.r2 + 1).map((r) => r.id);
    if (!ids.length) return;

    const ok = await dialog.confirm({
      title: 'Удалить строки?',
      body: `Будет удалено строк: ${ids.length}. Отменить можно через Ctrl+Z.`,
      danger: true,
    });
    if (!ok) return;

    history
      .remove(ids)
      .then((res) => toast(`Удалено: ${res.deleted}`, 'ok'))
      .catch((e) => toast((e as Error).message, 'err'));
  };

  const handleOpenHistory = useCallback(() => setShowHistory(true), []);
  const handleAddRow = useCallback(
    (count = 1, afterId?: string) => {
      const rows = Array.from({ length: count }, () => ({ fields: {} }));
      onCreate(rows, afterId);
    },
    [onCreate]
  );
  const handleAddColumn = useCallback(async () => {
    if (!effectiveCanEdit) return;
    const count = (columns.data?.length ?? 0) + 1;
    const name = await dialog.prompt({
      title: 'Новая колонка',
      placeholder: `Колонка ${count}`,
      value: `Колонка ${count}`,
    });
    if (name === null) return;
    addColumn.mutate({
      label: name.trim() || `Колонка ${count}`,
    });
  }, [effectiveCanEdit, columns.data, dialog, addColumn]);

  const handleReorderColumns = useCallback(
    (colIds: string[]) => {
      if (!effectiveCanEdit) return;
      reorderCols.mutate(colIds);
    },
    [effectiveCanEdit, reorderCols],
  );

  const handleMoveColumn = useCallback(
    (col: Column, dir: 'left' | 'right') => {
      if (!effectiveCanEdit || !columns.data) return;
      const newIds = moveColumnByDirection(columns.data, col.fields.colId, dir);
      reorderCols.mutate(newIds);
    },
    [effectiveCanEdit, columns.data, reorderCols],
  );

  const handleInsertColumnAt = useCallback(
    async (pos: number) => {
      if (!effectiveCanEdit) return;
      const count = (columns.data?.length ?? 0) + 1;
      const name = await dialog.prompt({
        title: 'Новая колонка',
        placeholder: `Колонка ${count}`,
        value: `Колонка ${count}`,
      });
      if (name === null) return;
      addColumn.mutate({
        label: name.trim() || `Колонка ${count}`,
        position: pos,
      });
    },
    [effectiveCanEdit, columns.data, dialog, addColumn],
  );
  const handleResizeColumn = useCallback(
    (colId: string, width: number | null) => resizeColumn.mutate({ colId, width }),
    [resizeColumn],
  );
  const handleResizeRow = useCallback(
    (recordId: string, height: number | null) => resizeRow.mutate({ recordId, height }),
    [resizeRow],
  );
  const handleAutoWidth = useCallback(
    (colId: string) => resizeColumn.mutate({ colId, width: null }),
    [resizeColumn],
  );
  const handleAutoHeight = useCallback(
    (recordId: string) => resizeRow.mutate({ recordId, height: null }),
    [resizeRow],
  );
  const handleRenameColumn = useCallback(
    async (column: Column) => {
      const next = await dialog.prompt({
        title: 'Переименовать колонку',
        body: `ID колонки: ${column.fields.colId}. Название — то, что видит пользователь.`,
        value: column.fields.label,
      });
      if (next?.trim()) {
        renameColumn.mutate({ colId: column.fields.colId, label: next.trim() });
      }
    },
    [dialog, renameColumn],
  );
  const handleDeleteColumn = useCallback(
    async (column: Column) => {
      const ok = await dialog.confirm({
        title: 'Удалить колонку?',
        body: `«${column.fields.label}» будет удалена. Данные во всех строках стерутся.`,
        danger: true,
      });
      if (ok) deleteColumn.mutate(column.fields.colId);
    },
    [dialog, deleteColumn],
  );

  const setColumnTypeFor = useCallback(
    (column: Column, type: ColumnType) => {
      // 1. Оптимистично обновляем тип колонки в кеше для мгновенного отображения в таблице
      qc.setQueryData<Column[]>(keys.columns(docId, activeId), (prev) =>
        prev?.map((c) =>
          c.fields.colId === column.fields.colId
            ? { ...c, fields: { ...c.fields, type } }
            : c,
        ),
      );

      // 2. Отправляем запрос на сервер
      api
        .updateColumn(docId, activeId, column.fields.colId, { type })
        .then(() => {
          qc.invalidateQueries({ queryKey: keys.columns(docId, activeId) });
          toast(`Тип изменён на «${COLUMN_TYPE_LABELS[type]}»`, 'ok');
        })
        .catch((e) => {
          qc.invalidateQueries({ queryKey: keys.columns(docId, activeId) });
          toast(`Не удалось изменить тип: ${(e as Error).message}`, 'err');
        });
    },
    [docId, activeId, qc, toast],
  );

  // Ctrl+F открывает поиск по листу.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.code === 'KeyF') {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (doc.isLoading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: R.textMuted }}>
        <Spinner /> Загрузка…
      </div>
    );
  }

  if (doc.error) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: R.danger }}>
        Ошибка: {(doc.error as Error).message}
      </div>
    );
  }

  const DocIcon = docIcon(doc.data?.icon);
  const hasTable = !!activeId;

  const fileMenu: MenuEntry[] = [
    ...(canEdit
      ? [
          { label: 'Изменить документ', icon: Pencil, onClick: () => setShowProps(true) } as MenuEntry,
          {
            label: 'Создать копию таблицы…',
            icon: Copy,
            onClick: async () => {
              const copyName = await dialog.prompt({
                title: 'Создать копию таблицы',
                value: `${doc.data?.name || 'Таблица'} (копия)`,
              });
              if (copyName === null) return;
              duplicateDoc.mutate(copyName.trim() || undefined);
            },
          } as MenuEntry,
        ]
      : []),
    ...(hasTable && activeTable && (effectiveCanEdit || canManageTableAccess)
      ? [
          {
            label: `Переименовать лист «${activeTable.label}»…`,
            icon: Pencil,
            onClick: () => handleRenameTable(activeTable),
          } as MenuEntry,
          {
            label: `Создать копию листа «${activeTable.label}»`,
            icon: Copy,
            onClick: async () => {
              const copyLabel = await dialog.prompt({
                title: 'Создать копию листа',
                value: `${activeTable.label} (копия)`,
              });
              if (copyLabel === null) return;
              duplicateTable.mutate({ tableId: activeTable.id, label: copyLabel.trim() || undefined });
            },
          } as MenuEntry,
        ]
      : []),
    ...(canEdit
      ? [{ label: 'Поделиться', icon: Share2, onClick: () => setShowShare(true) } as MenuEntry]
      : []),
    ...(canManageTableAccess && hasTable && activeTable
      ? [
          {
            label: `Доступ к листу «${activeTable.label}»…`,
            icon: Shield,
            onClick: () => setAccessModalTable({ id: activeTable.id, label: activeTable.label }),
          } as MenuEntry,
        ]
      : []),
    ...(effectiveCanEdit
      ? [{ label: 'Импорт из Excel', icon: Upload, onClick: () => setShowImport(true) } as MenuEntry]
      : []),
    {
      label: 'Скачать',
      icon: Download,
      submenu: [
        {
          label: 'CSV (.csv)',
          disabled: !hasTable,
          onClick: () => window.open(api.exportCsvUrl(docId, activeId), '_blank'),
        },
      ],
    },
    ...(canEdit
      ? [
          { type: 'divider' } as MenuEntry,
          {
            label: 'Удалить документ',
            icon: Trash2,
            danger: true,
            onClick: async () => {
              const ok = await dialog.confirm({
                title: 'Удалить документ?',
                body:
                  `«${doc.data?.name}» будет удалён целиком, вместе со всеми ` +
                  'таблицами. Восстановить нельзя.',
                danger: true,
              });
              if (ok) removeDoc.mutate();
            },
          } as MenuEntry,
        ]
      : []),
  ];

  const dataMenu: MenuEntry[] = [
    {
      label: 'Поиск по таблице',
      icon: Search,
      shortcut: 'Ctrl + F',
      disabled: !hasTable,
      onClick: () => setShowSearch(true),
    },
    {
      label: 'Сбросить сортировку и фильтры',
      icon: Filter,
      disabled: !hasTable || !reordered,
      onClick: () => {
        setQuery('');
        setSort(null);
        setFilters(new Map());
      },
    },
    ...(hiddenColIds.size > 0
      ? [
          {
            label: `Показать все скрытые колонки (${hiddenColIds.size})`,
            icon: Eye,
            onClick: unhideAllColumns,
          } as MenuEntry,
        ]
      : []),
    { type: 'divider' },
    {
      label: 'Добавить строку',
      icon: Rows3,
      disabled: !effectiveCanEdit || !hasTable,
      onClick: () => onCreate([{ fields: {} }]),
    },
    {
      label: 'Добавить колонку',
      icon: Columns3,
      disabled: !effectiveCanEdit || !hasTable,
      onClick: () => addColumn.mutate(),
    },
  ];

  const setColumnType = (type: ColumnType) => {
    if (activeColumn) setColumnTypeFor(activeColumn, type);
  };

  const formatMenu: MenuEntry[] = [
    {
      label: activeColumn ? `Тип: ${COLUMN_TYPE_LABELS[activeColumn.fields.type]}` : 'Тип',
      icon: Type,
      disabled: !activeColumn || activeColumn.fields.isFormula,
      submenu: COLUMN_TYPES.map((type) => ({
        label: COLUMN_TYPE_LABELS[type],
        icon: activeColumn?.fields.type === type ? Check : undefined,
        onClick: () => setColumnType(type),
      })),
    },
    { type: 'divider' },
    {
      label: 'Условное форматирование…',
      icon: Paintbrush,
      disabled: !hasTable,
      onClick: () => setShowConditional(true),
    },
  ];

  const getTableMenuEntries = (table: typeof tables[0]): MenuEntry[] => [
    { type: 'header', label: table.label },
    ...(canManageTableAccess
      ? [
          {
            label: 'Настройки доступа листа…',
            icon: Shield,
            onClick: () => setAccessModalTable({ id: table.id, label: table.label }),
          } as MenuEntry,
          { type: 'divider' } as MenuEntry,
        ]
      : []),
    {
      label: 'Переименовать лист…',
      icon: Pencil,
      disabled: !table.canEdit && !canManageTableAccess,
      onClick: () => handleRenameTable(table),
    },
    {
      label: 'Создать копию листа',
      icon: Copy,
      disabled: !table.canEdit && !canManageTableAccess,
      onClick: async () => {
        const copyLabel = await dialog.prompt({
          title: 'Создать копию листа',
          value: `${table.label} (копия)`,
        });
        if (copyLabel === null) return;
        duplicateTable.mutate({ tableId: table.id, label: copyLabel.trim() || undefined });
      },
    },
    { type: 'divider' },
    {
      label: 'Удалить лист',
      icon: Trash2,
      danger: true,
      disabled: !canManageTableAccess || tables.length <= 1,
      onClick: async () => {
        const ok = await dialog.confirm({
          title: 'Удалить лист?',
          body: `«${table.label}» и все данные листа будут удалены. Восстановить нельзя.`,
          danger: true,
        });
        if (ok) removeTable.mutate(table.id);
      },
    },
  ];

  return (
    <>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          minHeight: 56,
          padding: '8px 20px',
          borderBottom: ft.strong,
          flex: '0 0 auto',
          gap: 16,
          background: R.bg,
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flexWrap: 'wrap' }}
        >
          <a
            href="/portal"
            title="Перейти к порталу государственных реестров"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              height: 28,
              padding: '0 10px',
              borderRadius: 5,
              border: ft.edge,
              background: R.bgSubtle,
              color: R.textMuted,
              fontSize: 12,
              fontWeight: 600,
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = R.bgElevated;
              e.currentTarget.style.borderColor = R.accent;
              e.currentTarget.style.color = R.accentText;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = R.bgSubtle;
              e.currentTarget.style.borderColor = '';
              e.currentTarget.style.color = R.textMuted;
            }}
          >
            <ArrowLeft size={13} />
            <span>Портал</span>
          </a>

          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 10,
              minWidth: 0,
              maxWidth: 'min(46vw, 520px)',
            }}
          >
            <span style={{ ...label, whiteSpace: 'nowrap' }}>
              {doc.data?.section?.name ? `${doc.data.section.name} /` : 'Документ /'}
            </span>

            <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <DocIcon size={16} color={R.accent} />
              <InlineEdit
                value={doc.data?.name ?? ''}
                disabled={!canEdit}
                onSave={(name) => renameDoc.mutate(name)}
                style={{
                  fontWeight: 800,
                  fontSize: 19,
                  letterSpacing: '-.015em',
                  color: R.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                editStyle={{ fontWeight: 800, fontSize: 19, width: 280 }}
              />
            </span>
          </div>

          <div
            className="rt-toolbar"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              marginLeft: 8,
              maxWidth: '100%',
              overflowX: 'auto',
            }}
          >
            <MenuButton label="Файл" items={fileMenu} />
            <MenuButton label="Данные" items={dataMenu} />
            <MenuButton label="Формат" items={formatMenu} disabled={!effectiveCanEdit} />
          </div>
        </div>

        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            flexWrap: 'wrap',
          }}
        >
          {history.busy && <Spinner />}

          {savedAt && (
            <span style={{ fontSize: 11.5, color: R.textMuted, whiteSpace: 'nowrap' }}>
              Сохранено {savedAt}
            </span>
          )}

          {!effectiveCanEdit && (
            <span
              style={{
                padding: '3px 8px',
                borderRadius: 4,
                background: R.bgSubtle,
                border: ft.hair,
                fontSize: 11.5,
                color: R.textMuted,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
              }}
              title="На этом листе у вас режим просмотра"
            >
              <Shield size={12} style={{ color: R.accent }} /> Только чтение
            </span>
          )}

          <button
            style={btnOutline}
            disabled={!hasTable}
            onClick={() => setShowHistory((v) => !v)}
            title="Лента правок таблицы"
          >
            История
          </button>

          {canManageTableAccess && hasTable && activeTable && (
            <button
              style={btnOutline}
              onClick={() => setAccessModalTable({ id: activeTable.id, label: activeTable.label })}
              title="Настроить доступ к текущему листу (ведомству)"
            >
              <Shield size={13} style={{ color: R.accent }} /> Доступ к листу
            </button>
          )}

          {canEdit && (
            <button
              style={btnAccent}
              onClick={() => setShowShare(true)}
              title="Создать read-only публичную версию документа"
            >
              <Share2 size={13} /> Опубликовать
            </button>
          )}
        </div>
      </header>

      {tables.length > 0 && (
        <div
          className="rt-scroll"
          style={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 2,
            height: 36,
            padding: '0 16px',
            background: R.bg,
            borderBottom: ft.hair,
            flex: '0 0 auto',
            overflowX: 'auto',
          }}
        >
          {tables.map((table) => {
            const active = table.id === activeId;

            return (
              <div
                key={table.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: `2px solid ${active ? R.accent : 'transparent'}`,
                  marginBottom: -1,
                }}
                onContextMenu={(e) => tabMenu.open(e, getTableMenuEntries(table))}
              >
                <button
                  onClick={() => navigate(`/d/${docId}/t/${table.id}`)}
                  onDoubleClick={() => {
                    if (table.canEdit || canManageTableAccess) {
                      handleRenameTable(table);
                    }
                  }}
                  title={
                    canManageTableAccess || table.canEdit
                      ? `${table.label} · Двойной клик — переименовать, стрелка или правый клик — меню`
                      : `${table.label} (только чтение)`
                  }
                  style={{
                    padding: '0 8px 0 10px',
                    height: '100%',
                    fontSize: 12.5,
                    fontWeight: active ? 600 : 400,
                    background: active ? R.accentSubtle : 'transparent',
                    color: active ? R.text : R.textMuted,
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                  className={`rt-sheet-tab ${active ? 'rt-sheet-tab--active' : ''}`}
                >
                  <span>{table.label}</span>
                  {table.hasCustomAccess && (
                    <span
                      title="Лист с персональными правами доступа"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: R.accent,
                        opacity: 0.9,
                      }}
                    >
                      <Shield size={11} />
                    </span>
                  )}
                  {active && (table.canEdit || canManageTableAccess) && (
                    <span
                      role="button"
                      tabIndex={0}
                      title="Меню листа"
                      onClick={(e) => {
                        e.stopPropagation();
                        tabMenu.open(e, getTableMenuEntries(table));
                      }}
                      className="rt-sheet-tab-chevron"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 16,
                        height: 16,
                        borderRadius: 3,
                        color: R.textMuted,
                        opacity: 0.8,
                        marginLeft: 1,
                      }}
                    >
                      <ChevronDown size={11} />
                    </span>
                  )}
                </button>
              </div>
            );
          })}

          {canManageTableAccess && (
            <button
              onClick={() => setShowNewSheet(true)}
              title="Новый лист"
              className="rt-sheet-tab-add"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '0 10px',
                fontSize: 12.5,
                color: R.textMuted,
                background: 'transparent',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }}
            >
              <Plus size={12} /> лист
            </button>
          )}
        </div>
      )}

      {/* Сетка со статусной строкой слева, боковые панели — рядом в строке. */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            position: 'relative',
          }}
        >
          {/* Строка активных фильтров — видна, только когда что-то включено. */}
          {reordered && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minHeight: 40,
                padding: '6px 20px',
                borderBottom: ft.hair,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ ...label, marginRight: 4 }}>Фильтры</span>

              {query.trim() && (
                <button
                  style={chip(true)}
                  onClick={() => {
                    setQuery('');
                    setShowSearch(false);
                  }}
                >
                  Поиск: {query.trim()} <span aria-hidden>✕</span>
                </button>
              )}

              {sort && (
                <button style={chip(false)} onClick={() => setSort(null)}>
                  Сортировка: {columnLabel(sort.colId)} {sort.dir === 'asc' ? '↑' : '↓'}{' '}
                  <span aria-hidden>✕</span>
                </button>
              )}

              {[...filters.entries()].map(([colId, values]) => (
                <button
                  key={colId}
                  style={chip(false)}
                  title={[...values].join(', ')}
                  onClick={() => setFilter(colId, null)}
                >
                  {columnLabel(colId)}:{' '}
                  {values.size === 1
                    ? [...values][0] || 'пусто'
                    : `${values.size} ${plural(values.size, 'значение', 'значения', 'значений')}`}{' '}
                  <span aria-hidden>✕</span>
                </button>
              ))}

              <span style={{ marginLeft: 'auto', fontSize: 11.5, color: R.textMuted }}>
                Показано {view.length} из {records.data?.length ?? 0} ·{' '}
                <button
                  style={{
                    color: R.accentText,
                    fontSize: 11.5,
                    padding: 0,
                    background: 'transparent',
                  }}
                  onClick={() => {
                    setQuery('');
                    setSort(null);
                    setSearchColId(null);
                    setFilters(new Map());
                  }}
                >
                  сбросить
                </button>
              </span>
            </div>
          )}

          {/* Поиск всплывает над таблицей справа, а не занимает целую полосу. */}
          {showSearch && (
            <div
              style={{
                position: 'absolute',
                top: 6,
                right: 12,
                zIndex: 40,
                background: R.bgPanel,
                border: `1px solid ${R.border}`,
                boxShadow: shadow.dropdown,
                padding: '6px 8px',
                display: 'flex',
                gap: 6,
                alignItems: 'center',
                animation: 'rtIn .15s ease',
              }}
            >
              <Search size={13} color={R.textMuted} />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setShowSearch(false);
                    setQuery('');
                    setSearchColId(null);
                  }
                }}
                placeholder="Поиск в таблице…"
                style={{
                  width: 240,
                  padding: '6px 4px',
                  fontSize: 13,
                  background: 'transparent',
                  border: 'none',
                  color: R.text,
                }}
              />
              <span style={{ width: 1, height: 20, background: R.grid }} />

              <span style={{ width: 170 }} title="Искать в колонке">
                <Select
                  compact
                  value={searchColId ?? '__all__'}
                  onChange={(v) => setSearchColId(v === '__all__' ? null : v)}
                  options={[
                    { value: '__all__', label: 'По всем колонкам' },
                    ...(columns.data ?? []).map((c) => ({
                      value: c.fields.colId,
                      label: c.fields.label,
                    })),
                  ]}
                />
              </span>

              <span style={{ fontSize: 11, color: R.textMuted, minWidth: 32 }}>
                {query ? view.length : ''}
              </span>
              <button
                onClick={() => {
                  setShowSearch(false);
                  setQuery('');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 22,
                  height: 22,
                  color: R.textSecondary,
                  background: 'transparent',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = R.bgElevated)}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <X size={13} />
              </button>
            </div>
          )}

          {effectiveCanEdit && (
            <FormatToolbar
              docId={docId}
              tableId={activeId}
              columns={visibleColsList}
              records={view}
              sel={sel}
              canUndo={history.canUndo > 0}
              canRedo={history.canRedo > 0}
              undoLabel={history.undoLabel}
              redoLabel={history.redoLabel}
              onUndo={() => history.undo()}
              onRedo={() => history.redo()}
              onSearch={() => setShowSearch(true)}
              onClearSortFilter={() => {
                setQuery('');
                setSort(null);
                setFilters(new Map());
              }}
              sortFilterActive={reordered}
              onAddRow={() => onCreate([{ fields: {} }])}
              onAddColumn={() => addColumn.mutate()}
              onInsertImage={insertImage}
              onInsertLink={() => setShowLink(true)}
            />
          )}

          <FormulaBar
            columns={visibleColsList}
            records={view}
            sel={sel}
            onPatch={onPatch}
            canEdit={effectiveCanEdit}
          />

          {(reordered || hiddenColIds.size > 0) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minHeight: 40,
                padding: '6px 20px',
                borderBottom: ft.hair,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ ...label, marginRight: 4 }}>
                {reordered ? 'Фильтры' : 'Вид'}
              </span>

              {hiddenColIds.size > 0 && (
                <button
                  style={{
                    ...chip(true),
                    background: 'rgba(230, 126, 34, 0.12)',
                    borderColor: 'rgba(230, 126, 34, 0.35)',
                    color: '#d35400',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                  title="Нажмите, чтобы показать все скрытые колонки"
                  onClick={unhideAllColumns}
                >
                  <EyeOff size={12} style={{ marginRight: 5 }} />
                  Скрыто колонок: {hiddenColIds.size} <span style={{ marginLeft: 5 }}>✕</span>
                </button>
              )}

              {query.trim() && (
                <button style={chip(true)} onClick={() => { setQuery(''); setShowSearch(false); }}>
                  Поиск: {query.trim()} <span aria-hidden>✕</span>
                </button>
              )}

              {sort && (
                <button style={chip(false)} onClick={() => setSort(null)}>
                  Сортировка: {columnLabel(sort.colId)} {sort.dir === 'asc' ? '↑' : '↓'}{' '}
                  <span aria-hidden>✕</span>
                </button>
              )}

              {[...filters.entries()].map(([colId, values]) => (
                <button
                  key={colId}
                  style={chip(false)}
                  title={[...values].join(', ')}
                  onClick={() => setFilter(colId, null)}
                >
                  {columnLabel(colId)}:{' '}
                  {values.size === 1
                    ? [...values][0] || 'пусто'
                    : `${values.size} ${plural(values.size, 'значение', 'значения', 'значений')}`}{' '}
                  <span aria-hidden>✕</span>
                </button>
              ))}

              <span style={{ marginLeft: 'auto', fontSize: 11.5, color: R.textMuted }}>
                Показано {view.length} из {records.data?.length ?? 0}
                {hiddenColIds.size > 0 && ` · Колонок: ${visibleColsList.length} из ${(columns.data ?? []).length}`} ·{' '}
                <button
                  onClick={() => {
                    setQuery('');
                    setSort(null);
                    setFilters(new Map());
                    unhideAllColumns();
                  }}
                  style={{ color: R.accentText, fontSize: 11.5, padding: 0, background: 'transparent' }}
                >
                  сбросить всё
                </button>
              </span>
            </div>
          )}

          {!hasTable ? (
            <div style={{ padding: 40, textAlign: 'center', color: R.textMuted }}>
              Откройте таблицу
            </div>
          ) : columns.isLoading || records.isLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: R.textMuted }}>
              <Spinner /> Загружаем лист…
            </div>
          ) : (
            <Grid
              columns={columns.data ?? []}
              records={view}
              editable={effectiveCanEdit}
              hiddenColIds={hiddenColIds}
              onToggleHideColumn={toggleHideColumn}
              onUnhideAllColumns={unhideAllColumns}
              onUnhideColumns={unhideColumns}
              onPatch={onPatch}
              onCreate={onCreate}
              onSelection={setSel}
              onOpenHistory={handleOpenHistory}
              onOpenColumn={effectiveCanEdit ? setColumnFor : undefined}
              computed={computed}
              styles={styleMap}
              merges={reordered ? undefined : merges.data}
              conditional={conditional.data}
              colWidths={colWidths}
              rowHeights={rowHeights}
              onResizeColumn={effectiveCanEdit ? handleResizeColumn : undefined}
              onResizeRow={effectiveCanEdit ? handleResizeRow : undefined}
              onAddRow={handleAddRow}
              onDeleteRows={removeRows}
              onAddColumn={handleAddColumn}
              onRenameColumn={handleRenameColumn}
              onDeleteColumn={handleDeleteColumn}
              onSetColumnType={setColumnTypeFor}
              onAutoWidth={handleAutoWidth}
              onAutoHeight={handleAutoHeight}
              onReorderColumns={effectiveCanEdit ? handleReorderColumns : undefined}
              onMoveColumn={effectiveCanEdit ? handleMoveColumn : undefined}
              onInsertColumnAt={effectiveCanEdit ? handleInsertColumnAt : undefined}
              sort={sort}
              onSort={setSort}
              filters={filters}
              valuesFor={valuesFor}
              onFilter={setFilter}
            />
          )}

          <StatusBar
            stats={selectionStats}
            rowCount={view.length}
            colCount={visibleColsList.length}
            editorsOnline={Math.max(0, viewers.length - 1)}
          />
        </div>

        {showHistory && (
          <HistoryPanel
            docId={docId}
            tableId={activeId}
            columns={columns.data ?? []}
            records={view}
            onClose={() => setShowHistory(false)}
            onGoToCell={(recordId, colId) => {
              const r = view.findIndex((rec) => rec.id === recordId);
              if (hiddenColIds.has(colId)) {
                unhideColumns([colId]);
              }
              const visibleNow = (columns.data ?? []).filter(
                (col) => !hiddenColIds.has(col.fields.colId) || col.fields.colId === colId,
              );
              const c = visibleNow.findIndex((col) => col.fields.colId === colId);
              if (r >= 0 && c >= 0) setSel({ r1: r, c1: c, r2: r, c2: c });
            }}
          />
        )}

        {columnFor && (
          <ColumnPanel
            docId={docId}
            tableId={activeId}
            column={columnFor}
            onClose={() => setColumnFor(null)}
          />
        )}
      </div>

      {showImport && (
        <ImportDialog docId={docId} tableId={activeId} onClose={() => setShowImport(false)} />
      )}

      {showConditional && (
        <ConditionalDialog
          docId={docId}
          tableId={activeId}
          columns={columns.data ?? []}
          onClose={() => setShowConditional(false)}
        />
      )}

      {showShare && <SharePanel docId={docId} onClose={() => setShowShare(false)} />}

      {showProps && doc.data && (
        <DocPropertiesModal
          doc={{
            id: docId,
            name: doc.data.name,
            icon: doc.data.icon,
            sectionId: doc.data.section?.id ?? null,
            tableCount: tables.length,
            shareLinks: 0,
            published: false,
            updatedAt: new Date().toISOString(),
          }}
          onClose={() => setShowProps(false)}
        />
      )}

      {showLink && (
        <LinkModal
          onClose={() => setShowLink(false)}
          onApply={(formula) => {
            if (!sel) return;
            const rec = view[sel.r1];
            const col = (columns.data ?? [])[sel.c1];
            if (rec && col) {
              history
                .patch([{ id: rec.id, fields: { [col.fields.colId]: formula } }], 'Вставить ссылку')
                .catch((e) => toast((e as Error).message, 'err'));
            }
          }}
        />
      )}

      {showNewSheet && (
        <NewSheetModal
          docId={docId}
          onClose={() => setShowNewSheet(false)}
          onCreated={(id) => {
            setShowNewSheet(false);
            navigate(`/d/${docId}/t/${id}`);
          }}
        />
      )}

      {accessModalTable && (
        <TableAccessModal
          docId={docId}
          tableId={accessModalTable.id}
          tableLabel={accessModalTable.label}
          onClose={() => setAccessModalTable(null)}
        />
      )}
    </>
  );
}
